import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import MainContainer from "../../components/MainContainer";

import {
  selectDeleteTrainModelPending,
  selectExploreCollectionsKeyword,
  selectSelectedCollection,
  selectSelectedTrainModel,
  selectSubmitTrainModelForReviewPending,
  setSelectedCollection,
} from "../../store/slices/model/slice";
import {
  deleteTrainModel,
  getTrainModel,
  submitTrainModelForReview,
} from "../../store/slices/model/asyncThunks";

import {
  BackButton,
  ModelTitle,
  ConfigSection,
  SectionTitle,
  Label,
  TextInput,
  RequirementsSection,
  InputContainer,
  Row,
  Text,
  SkeletonLoader,
  SkeletonSection,
  SkeletonLoaderColumn,
  List,
  IconContainer,
  SvgContainer,
  ThinText,
  TextArea,
  SelectContainer,
  SectionHeader,
  SectionWrapper,
  DeleteButton,
  ActionButtons,
} from "./TrainingCompletedPage.styles";

import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as SearchSvg } from "../../assets/icons/search2.svg";
import { ReactComponent as PublicSvg } from "../../assets/icons/public.svg";
import { ReactComponent as MagicStickSvg } from "../../assets/icons/magicStick.svg";
import { ReactComponent as TrashSvg } from "../../assets/icons/trash.svg";
import { ReactComponent as CircleSvg } from "../../assets/icons/circle.svg";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import Button from "../../components/utils/Button/Button";
import { toast } from "../../components/modern-ui/sonner";
import {
  ALLOWED_TAGS,
  PREMIUM_PLAN_NAME,
  PRO_PLAN_NAME,
  TRAIN_MODEL_STATUS,
} from "../../utils/constants";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import Dropdown from "../../components/Dropdown/Dropdown";
import { selectUser } from "../../store/slices/user/slice";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import radioButtonAnimation from "../../assets/animations/radioButton.json";
import EditImageModal from "../../components/EditImageModal/EditImageModal";
import { isImageOutput } from "../../utils/utils";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";

const TrainingCompletedPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const [templateName, setTemplateName] = useState("");
  const [author, setAuthor] = useState("");
  const [categories, setCategories] = useState([]);
  const [description, setDescription] = useState("");

  const selectedTrainModel = useSelector(selectSelectedTrainModel);
  const isLoggedIn = useSelector(selectLoggedIn);

  const submitLoading = useSelector(selectSubmitTrainModelForReviewPending);

  const trainModelId = params.modelId;

  const [isChecked, setIsChecked] = useState(false);

  const selectedCollection = useSelector(selectSelectedCollection);
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);

  const savedKeyword = useSelector(selectExploreCollectionsKeyword);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deleteTrainModelLoading = useSelector(selectDeleteTrainModelPending);

  const user = useSelector(selectUser);

  const isProEnabled =
    [PRO_PLAN_NAME, PREMIUM_PLAN_NAME].includes(user?.subscription?.name) &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  useEffect(() => {
    if (selectedTrainModel && selectedTrainModel.data) {
      setTemplateName(selectedTrainModel.data.templateName);
      setAuthor(selectedTrainModel.data.author);
      setDescription(selectedTrainModel.data.description || "");
    }
  }, [selectedTrainModel]);

  useEffect(() => {
    if (isLoggedIn && trainModelId) {
      dispatch(getTrainModel(trainModelId));
    }
  }, [trainModelId, isLoggedIn, dispatch]);

  const handleBack = () => {
    navigate("/train");
  };

  const hasDefaultTemplateName = /new template/i.test(templateName);

  const actionSubmitForReview = () => {
    if (!categories || categories.length === 0) {
      toast.error("Please select at least one category");
      return;
    }

    if (!templateName || templateName.trim().length === 0) {
      toast.error("Please fill in template name");
      return;
    }

    if (hasDefaultTemplateName) {
      toast.warning(
        `Please change the template name — it still contains the default "New Template".`,
      );
      return;
    }

    // if (!description || description.trim().length === 0) {
    //   toast.error("Please provide usage instructions for others");
    //   return;
    // }

    // if (description.trim().length < 20) {
    //   toast.error("Usage instructions should be at least 20 characters");
    //   return;
    // }

    dispatch(
      submitTrainModelForReview({
        trainModelId,
        keywords: categories,
        templateName,
        ...(description && description.length && { description }),
        ...(user?.isVipCreator && isChecked && { addToVipCategory: true }),
      }),
    )
      .unwrap()
      .then(() => {
        navigate(`/train/completed/${trainModelId}`);
      })
      .catch(() => {
        toast.error(`Error occurred while submitting model for review`);
      });
  };

  const toCamelCase = (str) => {
    return str
      ?.toLowerCase()
      .split("_")
      .map((word, index) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleGenerateAgain = () => {
    const modelObject = {
      ...selectedTrainModel.data?.selectedCollection,
      regenerate: false,
      collectionId: selectedTrainModel.data?.selectedCollection._id,
    };

    dispatch(setSelectedCollection(modelObject));
    if (isImageOutput(modelObject.modelType ?? modelObject.modelName)) {
      setIsEditImageModalOpen(true);
    } else {
      setIsGenerateCollectionModalOpen(true);
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteTrainModel(trainModelId)).unwrap();
      navigate("/train");
      localStorage.setItem("activeTabIndex", "2");
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    } catch (error) {
      console.error("Failed to delete train model:", error);
    }
    setIsDeleteModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  // Show loading state
  if (selectedTrainModel.loading) {
    return (
      <MainContainer>
        <BackButton onClick={handleBack}>
          <ChevronLeft />
          Back to Train
        </BackButton>
        <SkeletonLoader $height="39px" $width="250px" />
        <SkeletonLoaderColumn>
          <IconContainer>
            <SkeletonLoader $height="44px" $width="44px" $radius="50%" />
            <SkeletonLoader $height="22px" $width="180px" />
          </IconContainer>
          <Row>
            <SkeletonSection $height="fit-content">
              <SectionHeader>
                <SectionTitle>Publish</SectionTitle>
                <PublicSvg />
              </SectionHeader>
              <SectionWrapper>
                <SkeletonLoader
                  $height="17px"
                  $width="94px"
                  style={{ marginBottom: -8 }}
                />
                <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
                <SkeletonLoader
                  $height="16px"
                  $width="100px"
                  style={{ marginBottom: -8 }}
                />
                <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
                <SkeletonLoader
                  $height="17px"
                  $width="100px"
                  style={{ marginBottom: -8 }}
                />
                <SkeletonLoader
                  $height="44px"
                  $width="100%"
                  $radius="12px"
                  style={{ marginBottom: -8 }}
                />

                <SkeletonLoader $height="17px" $width="257.5px" />
                <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
                <SkeletonLoader
                  $height="17px"
                  $width="184px"
                  style={{ marginBottom: -8 }}
                />
                <SkeletonLoader
                  $height="100px"
                  $width="100%"
                  $radius="12px"
                  style={{ marginBottom: -8 }}
                />
                <SkeletonLoader $height="17px" $width="384px" />

                <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
              </SectionWrapper>
            </SkeletonSection>

            <SkeletonSection $height="fit-content">
              <SectionHeader>
                <SectionTitle>Review Process</SectionTitle>
                <SearchSvg />
              </SectionHeader>

              <SectionWrapper>
                <SkeletonLoader $height="18px" $width="100%" />
                <SkeletonLoader $height="18px" $width="100%" />
                <SkeletonLoader $height="36px" $width="100%" />
                <SkeletonLoader $height="36px" $width="100%" />
                <SkeletonLoader $height="18px" $width="100%" />
                <SkeletonLoader $height="18px" $width="100%" />
                <SkeletonLoader $height="18px" $width="100%" />
                <SkeletonLoader $height="44px" $width="100%" $radius="100px" />
              </SectionWrapper>
            </SkeletonSection>
          </Row>
        </SkeletonLoaderColumn>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft />
        Back to Train
      </BackButton>

      <ModelTitle>{templateName ?? ""}</ModelTitle>
      <IconContainer>
        <SvgContainer>
          <CircleSvg />
        </SvgContainer>

        <SectionTitle>Training Completed!</SectionTitle>
      </IconContainer>

      <Row>
        <ConfigSection>
          <SectionHeader>
            <SectionTitle>Publish</SectionTitle>
            <PublicSvg />
          </SectionHeader>

          <SectionWrapper>
            <InputContainer>
              <Label>Template name</Label>
              <TextInput
                disabled={selectedTrainModel.data.status !== "COMPLETED"}
                type="text"
                placeholder="New Template 01"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </InputContainer>

            <InputContainer>
              <Label>Author</Label>
              <TextInput
                disabled={true}
                type="text"
                placeholder="@author"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
              />
            </InputContainer>

            {selectedTrainModel.data?.status ===
              TRAIN_MODEL_STATUS.COMPLETED && (
              <>
                <InputContainer>
                  <Label>Tags</Label>
                  <Dropdown
                    options={ALLOWED_TAGS}
                    placeholder="Select tags for template"
                    defaultValue={savedKeyword}
                    onChange={(value) => {
                      setCategories(value);
                    }}
                    isProEnabled={isProEnabled}
                    fullWidth
                    multipleSelect
                    min={1}
                    max={user?.isVipCreator ? 6 : 5}
                  />
                  <ThinText>{`Minimum 1, maximum ${user?.isVipCreator ? 6 : 5} categories`}</ThinText>
                </InputContainer>

                {user?.isVipCreator &&
                  selectedTrainModel?.data?.type !== "image" && (
                    <SelectContainer onClick={() => setIsChecked(!isChecked)}>
                      <LottieIcon
                        animationData={radioButtonAnimation}
                        speed={2}
                        trigger="toggle-reverse"
                        isActive={isChecked}
                        size={22}
                        strokeColor={!isChecked ? "#2877d9" : "#ffffff"}
                      />
                      Include this template in VIP Section
                    </SelectContainer>
                  )}

                <InputContainer>
                  <Label>Usage Instructions</Label>
                  <TextArea
                    placeholder="Explain how others should use your model to get the best results..."
                    value={description}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) {
                        setDescription(e.target.value);
                      }
                    }}
                    rows={4}
                    maxLength={200}
                  />
                  <ThinText>
                    {description.length}/200 characters - Guide users on how to
                    use your model effectively
                  </ThinText>
                </InputContainer>
              </>
            )}

            {selectedTrainModel.data?.status ===
            TRAIN_MODEL_STATUS.COMPLETED ? (
              <Button
                loading={submitLoading}
                title={"Submit for Review"}
                onClick={actionSubmitForReview}
                style={{ width: "100%" }}
              />
            ) : (
              <Text $offDot>
                Train model status:{" "}
                {toCamelCase(selectedTrainModel.data?.status)}
              </Text>
            )}
          </SectionWrapper>
        </ConfigSection>

        <RequirementsSection>
          <SectionHeader>
            <SectionTitle>Review Process</SectionTitle>
            <SearchSvg />
          </SectionHeader>

          <SectionWrapper>
            <List>
              <Text>
                Template must pass our team’s review before becoming publicly
                available.
              </Text>

              <Text>
                When your template is used publicly, you earn credits based on
                its age:
              </Text>

              {user?.isVipCreator ? (
                <Text>
                  Earn 3 credits per use during the first 30 days, 2 credits per
                  use from days 31–60, and 1 credit per use after 60 days.
                </Text>
              ) : (
                <Text>
                  Earn 2 credits per use during the first 60 days, and 1 credit
                  per use after 60 days.
                </Text>
              )}

              <Text>
                VIP Creators receive higher earnings. Once you have 10+
                high-quality templates live, you will be eligible to apply to
                join VIP
              </Text>

              <Text>
                We review for quality standards and potential copyright
                infringement.
              </Text>

              <Text>
                Template approval is not guaranteed and we reserve final
                discretion.
              </Text>

              <Text>
                Once published, templates become permanently available.
              </Text>
            </List>

            <ActionButtons>
              <DeleteButton
                onClick={handleDelete}
                leftIcon={<TrashSvg width={15} height={15} />}
                title={"Delete"}
                style={{
                  borderRadius: "100px",
                  display: "flex",
                  width: "130px",
                  height: "44px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "12px",
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: "#f2efe8",
                  cursor: "pointer",
                  fontFamily: "Figtree, sans-serif",
                  fontSize: "16px",
                  fontWeight: 500,
                  letterSpacing: "-0.08px",
                  transition: "all 0.3s ease-in-out",
                }}
              />
              {selectedTrainModel.data?.selectedCollection && (
                <Button
                  leftIcon={<MagicStickSvg />}
                  title={"Use Your Model"}
                  onClick={handleGenerateAgain}
                  style={{ flex: 1 }}
                />
              )}
            </ActionButtons>
          </SectionWrapper>
        </RequirementsSection>
      </Row>

      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Model"
        isLoading={deleteTrainModelLoading}
        message="Are you sure you want to delete this train model? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      <EditImageModal
        isOpen={isEditImageModalOpen}
        collection={selectedCollection}
        onClose={() => setIsEditImageModalOpen(false)}
      />
    </MainContainer>
  );
};

export default TrainingCompletedPage;
